import {Badge, Button, Col, Input, message, Row, Space, Table, TableProps} from "antd";
import './WorkOnPractices.css'
import {useLocation, useNavigate, useParams} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {HttpService} from "../../../services/http/Http.service";
import {UtilsService} from "../../../services/Utils.service";
import {ArrowRightOutlined, CheckOutlined, DownloadOutlined, MinusOutlined, SearchOutlined} from "@ant-design/icons";
import DetailModal from "./DetailModal";
import Title from "antd/es/typography/Title";
import {PracticeExportService} from "../PracticeExport.service";
import ConfirmModal from "../../../components/confirmModal/ConfirmModal";
import {SelectAllButton} from "../SelectAllButton";
import {RoutingURL, SimulatedFileTS, StatusCodeE} from "../../../model/models";
import {combinatorColumns} from "../combinatorColumns";
import {HttpErrorService} from "../../../services/http/HttpError.service";
import {usePaginatedData} from "../../../services/hooks/usePaginatedData";
import {AuthService} from "../../../services/Auth.service";

export default function WorkOnPractices() {
    const {id} = useParams();
    const location = useLocation();
    const combinator = location.state?.combinator;
    const navigate = useNavigate();

    const {
        data: filteredData,
        totalItems,
        pageData,
        searchText,
        setSearchText,
        handleTableChange,
        reloadCurrentPage,
    } = usePaginatedData<SimulatedFileTS>({
        simulatorId: id,
        status: StatusCodeE.DA_LAVORARE,
        initialPageSize: 20
    });

    const [isUserDetalModalOpen, setIsUserDetalModalOpen] = useState(false)
    const [userId, setUserId] = useState<number>()
    const [username, setUsername] = useState<string>("")
    const [fiscalCode, setFiscalCode] = useState<string>("")
    const [type, setType] = useState<string>("")
    const [isRateApplied, setIsRateApplied] = useState<boolean>(false)
    const [isRateModalOpen, setIsRateModalOpen] = useState(false)
    const [isInterestsModalOpen, setIsInterestsModalOpen] = useState(false)
    const [combinatorData, setCombinatorData] = useState<any[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
    const [selectedTotal, setSelectedTotal] = useState(0);

    useEffect(() => {
        if (!combinator) {
            HttpService.getInstanceAxios().get(`/simulazione/${id}/statistiche`)
                .then(response => {
                    const item = response.data || {};

                    const {simulator, statistics} = item;
                    const {filters} = simulator;

                    let yearLabel = filters.years.join(" - ");
                    if (filters.checked) {
                        yearLabel += " (Anni indicati)";
                    }

                    let exemptionLabel = "Tutti";
                    if (filters.exemptionCodes?.length > 0) {
                        exemptionLabel = filters.exemptionCodes.join(" - ");
                    }
                    if (filters.exemptionCodeChecked) {
                        exemptionLabel += " (Codici indicati)";
                    }
                    setCombinatorData([{
                        key: simulator.id,
                        numberPositions: statistics.totalOccurrences,
                        numberAssisted: statistics.totalPerson,
                        exemptionCode: exemptionLabel,
                        year: yearLabel,
                        type: statistics.typeList?.join(" - "),
                        amount: "€ " + UtilsService.formatAmountRange([filters.minAmount, filters.maxAmount]),
                        ticket: "€ " + UtilsService.formatNumber(statistics.totalTicket),
                    }]);
                })
        } else {
            let yearLabel = "";
            let exemptionLabel = "";

            if (combinator?.title) {
                const title = combinator.title.replace("Combinatore ", "").trim();

                const match = title.match(/^(.*?)(?: \((.*?)\))?$/);
                const baseYear = match?.[1] ?? "";
                const suffix = match?.[2] ?? "";

                yearLabel = baseYear;
                if (suffix.includes("Anni")) {
                    yearLabel += " (Anni indicati)";
                }

                if (suffix.includes("Codici")) {
                    exemptionLabel = (combinator?.codes?.length ? combinator.codes.join(" - ") : "Tutti") + " (Codici indicati)";
                } else {
                    exemptionLabel = combinator?.codes?.length ? combinator.codes.join(" - ") : "Tutti";
                }
            } else {
                yearLabel = "";
                exemptionLabel = combinator?.codes?.length ? combinator.codes.join(" - ") : "Tutti";
            }

            setCombinatorData([{
                key: combinator?.id,
                numberPositions: combinator?.numberPositions,
                numberAssisted: combinator?.numberAssisted,
                exemptionCode: exemptionLabel,
                year: yearLabel,
                type: combinator?.typeList?.join(" - "),
                amount: "€ " + UtilsService.formatAmountRange(combinator?.amount),
                ticket: "€ " + UtilsService.formatNumber(combinator?.totalTicket),
            }]);
        }
    }, [combinator]);

    useEffect(() => {
        if (userId !== null && userId !== undefined) {
            setIsUserDetalModalOpen(true);
        }
    }, [userId]);

    const columns = [
        {
            title: "Codice Fiscale",
            dataIndex: "fiscalCode",
            key: "fiscalCode",
            sorter: true
        },
        {
            title: "Cognome Nome",
            dataIndex: "fullName",
            key: "fullName",
            sorter: true
        },
        {
            title: "Anno",
            dataIndex: "years",
            key: "years",
            render: (years: string[]) => years.sort((a, b) => parseInt(a) - parseInt(b)).join(" - ")
        },
        {
            title: "Codici esenzione",
            dataIndex: "exemptionCodes",
            key: "exemptionCodes",
            sorter: true,
            render: (codes: string[]) => codes.join(" - ")
        },
        {
            title: "Tipo",
            dataIndex: "type",
            key: "type"
        },
        {
            title: "Rate",
            dataIndex: "hasInstallment",
            key: "hasInstallment",
            render: (value: boolean) => (value ? <CheckOutlined style={{color: 'var(--main-color)'}}/> :
                <MinusOutlined/>),
        },
        {
            title: "PagoPA",
            dataIndex: "hasPayment",
            key: "hasPayment",
            render: (value: boolean) => (value ? <CheckOutlined style={{color: 'var(--main-color)'}}/> :
                <MinusOutlined/>),
        },
        {
            title: "Interessi",
            dataIndex: "totalInterest",
            key: "totalInterest",
            render: (value: number) => (value > 0 ? <CheckOutlined style={{color: 'var(--main-color)'}}/> :
                <MinusOutlined/>),
        },
        {
            title: "Importo totale",
            dataIndex: "totalSum",
            key: "totalSum",
            sorter: true,
            render: (value: number) => "€ " + UtilsService.formatNumber(value)
        },
        {
            title: "Azioni",
            key: "operation",
            render: (record: any) => (
                <Space>
                    <Button onClick={() => handleModal(record)} type={"primary"} icon={<ArrowRightOutlined/>}/>
                </Space>
            ),
        },
    ];

    const handleModal = (record: SimulatedFileTS) => {
        setUserId(record.personId)
        setUsername(record.fullName)
        setFiscalCode(record.fiscalCode)
        setType(record.type)
        setIsRateApplied(record.hasInstallment)
    }

    const [isExporting, setIsExporting] = useState(false);
    const [applyRateLoading, setApplyRateLoading] = useState(false);

    const applyRate = async () => {
        setApplyRateLoading(true);
        if (selectedRowKeys.length === 0) {
            message.error("Nessun utente selezionato.");
            setIsRateModalOpen(false);
            return;
        }

        const hasAlreadyApplied = filteredData.some(row =>
            selectedRowKeys.includes(row.personId) && row.hasInstallment
        );

        if (hasAlreadyApplied) {
            message.error("Rateizzazione già applicata per le pratiche selezionate");
            setIsRateModalOpen(false);
            return;
        }
        console.debug("Apply rate")
        try {
            await HttpService.getInstanceAxios().post(`/file-ts/${id}/apply-installment`, selectedRowKeys)
            message.success("Rate applicate con successo");
            setSelectedRowKeys([]);
            setSelectedTotal(0);
            await reloadCurrentPage()
        } catch (error) {
            HttpErrorService.handle("Errore durante l'applicazione delle rate", error, true)
        } finally {
            setApplyRateLoading(false);
        }
        setIsRateModalOpen(false);
    };

    const [applyInterestLoading, setApplyInterestLoading] = useState(false);


    const applyInterests = async () => {
        setApplyInterestLoading(true);
        if (selectedRowKeys.length === 0) {
            message.error("Nessun utente selezionato.");
            setIsInterestsModalOpen(false);
            return;
        }

        const hasAlreadyApplied = filteredData.some(row =>
            selectedRowKeys.includes(row.personId) && row.totalInterest > 0
        );

        if (hasAlreadyApplied) {
            message.error("Interessi già applicati sulle pratiche selezionate");
            setIsInterestsModalOpen(false);
            return;
        }
        console.debug("Apply interests")
        try {
            await HttpService.getInstanceAxios().post(`/file-ts/${id}/apply-interests`, selectedRowKeys)
            setSelectedRowKeys([]);
            setSelectedTotal(0);
            await reloadCurrentPage()
        } catch (error) {
            HttpErrorService.handle("Errore durante l'applicazione degli interessi", error, true)
        } finally {
            setApplyInterestLoading(false);
        }
        setIsInterestsModalOpen(false);
    };

    const rowSelection: TableProps<SimulatedFileTS>['rowSelection'] = {
        selectedRowKeys: selectedRowKeys,
        onChange: (selectedRowKeys: React.Key[], selectedRows: SimulatedFileTS[]) => {
            const total = selectedRows.reduce((sum, row) => sum + row.totalSum, 0);
            setSelectedTotal(total);
            setSelectedRowKeys(selectedRowKeys)
        },

    };

    const [onNextLoading, setonNextLoading] = useState(false);

    const onNext = async () => {
        setonNextLoading(true);
        if (selectedRowKeys.length > 0) {
            try {
                await HttpService.getInstanceAxios().post(`/file-ts/${id}/next-state`, selectedRowKeys)
                if (AuthService.isAdmin()) {
                    await reloadCurrentPage()
                }
                else {
                    let url: string = UtilsService.replaceId(RoutingURL.PRACTICES, id);
                    url = UtilsService.replaceStatus(url, StatusCodeE.IN_LAVORAZIONE);
                    navigate(url);
                }
            } catch (error) {
                HttpErrorService.handle("Errore durante il cambio stato", error, true)
            } finally {
                setonNextLoading(false);
            }
        } else message.error("Nessun utente selezionato.")
    }

    async function exportToExcel(xlsx: string) {
        if (!id) return;
        setIsExporting(true);
        try {
            await PracticeExportService.loadAndExportToExcel(Number.parseInt(id), StatusCodeE.DA_LAVORARE, xlsx);
        } finally {
            setIsExporting(false);
        }
    }

    return (
        <>
            <Row justify="space-between" align="middle">
                <Col>
                    <Input
                        style={{width: "400px"}}
                        size="middle"
                        allowClear
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        addonBefore={<SearchOutlined/>}
                        placeholder="Nome, Cognome, Codice Fiscale"
                    />
                </Col>
                <Col>
                    <Space>
                        <Button onClick={() => setIsInterestsModalOpen(true)} type="dashed">
                            Applica Interessi
                        </Button>
                        <Button onClick={() => setIsRateModalOpen(true)} type="dashed">
                            Applica Rateizzazione
                        </Button>
                        <Button
                            className="custom-label-color"
                            type="primary"
                            icon={<DownloadOutlined/>}
                            loading={isExporting}
                            onClick={() => exportToExcel('Report.xlsx')}
                        >
                            Esporta
                        </Button>
                    </Space>
                </Col>
            </Row>

            <Space size={30} direction={"vertical"} style={{width: "100%"}}>
                <Table
                    columns={combinatorColumns}
                    dataSource={combinatorData}
                    pagination={false}
                    bordered
                />
                <Space style={{display: "flex", justifyContent: "space-between"}}>
                    {id && <SelectAllButton
                        idSimulator={id}
                        status={StatusCodeE.DA_LAVORARE}
                        onSelected={(keys) => {
                            setSelectedRowKeys(keys);
                            const keyNumbers = keys.map(k => Number(k));
                            const total = filteredData
                                .filter(row => keyNumbers.includes(row.personId))
                                .reduce((sum, row) => sum + (row.totalSum || 0), 0);

                            setSelectedTotal(total);
                        }}
                    />


                    }
                    <div>
                        Utenti selezionati: <Badge color={"#007A33FF"}
                                                   count={selectedRowKeys ? selectedRowKeys.length : 0} showZero={true}
                                                   overflowCount={9999}
                                                   style={{fontSize: "14px", transform: "scale(1.1)"}}/>
                    </div>
                </Space>
                <Table<SimulatedFileTS>
                    rowSelection={{type: "checkbox", ...rowSelection}}
                    rowKey={record => record.personId}
                    rowClassName={record => {
                        return record.hasPayment ? 'disabled-row' : '';
                    }}
                    dataSource={filteredData}
                    columns={columns}
                    pagination={{
                        total: totalItems,
                        showTotal: (total) => `Totale ${total} elementi`,
                        current: pageData.current,
                        pageSize: pageData.pageSize,
                        pageSizeOptions: ["10", "20", "50", "100", "250", "500"],
                        showSizeChanger: true,
                    }}
                    onChange={handleTableChange}
                />

            </Space>
            <Space style={{display: "flex", justifyContent: "flex-end"}}>
                <Button className="custom-label-color" onClick={onNext} size={"large"}
                        type={"primary"} loading={onNextLoading}>Continua</Button>
            </Space>
            <Title className={"custom-title"} level={5}>Importo selezionato:
                € {UtilsService.formatNumber(selectedTotal)}</Title>
            {isUserDetalModalOpen ?
                <DetailModal isOpen={isUserDetalModalOpen}
                             combinatorId={id}
                             setIsOpen={setIsUserDetalModalOpen}
                             userId={userId}
                             setUserId={setUserId}
                             username={username}
                             fiscalCode={fiscalCode}
                             type={type}
                             isRateApplied={isRateApplied}
                />
                : null
            }
            <ConfirmModal
                visible={isRateModalOpen}
                onConfirm={applyRate}
                onCancel={() => setIsRateModalOpen(false)}
                title="Applica rateizzazione"
                message="Sei sicuro di voler applicare la rateizzazione?"
                confirmText="Sì, applica"
                cancelText="Annulla"
                loading={applyRateLoading}
            />

            <ConfirmModal
                visible={isInterestsModalOpen}
                onConfirm={applyInterests}
                onCancel={() => setIsInterestsModalOpen(false)}
                title="Applica interessi"
                message="Sei sicuro di voler applicare gli interessi?"
                confirmText="Sì, applica"
                cancelText="Annulla"
                loading={applyInterestLoading}
            />

        </>
    )
}